import { getConfig } from '../../configs/config';
import messages from '../../constants/logMessages.json';
import {
  AnalyticsEventTypes,
  ApiComplete,
  ApiError,
  ApiStart,
  ApiTransactionComplete,
  ApiTransactionStart,
} from '../../types';
import logging, { getMessage } from '../logging/index';
import tools from '../tools/index';

const { infoHeading, warnHeading, infoBold, warnBold } = messages;

type ApiEvent =
  | ApiStart
  | ApiComplete
  | ApiTransactionStart
  | ApiTransactionComplete
  | ApiError;

/**
 * Logs a warning message.
 *
 * @param {keyof typeof messages} messageKey - The key for the log message.
 * @param {string} event - The event type.
 */
const logWarning = (messageKey: keyof typeof messages, event: string): void => {
  logging.info(getMessage(messageKey, [event]), warnHeading, warnBold);
};

/**
 * Logs a warning message for a missing milestone.
 *
 * @param {string} event - The event type.
 * @param {string} sanitisedApiName - The sanitised API name.
 * @param {string} requestId - The request ID.
 */
const logMissingMilestone = (
  event: string,
  sanitisedApiName: string,
  requestId: string
): void => {
  const messageKey: keyof typeof messages =
    event === AnalyticsEventTypes.ApiComplete ||
    event === AnalyticsEventTypes.TransactionComplete
      ? 'apiNameCompleteNotFound'
      : 'apiNameStartNotFound';
  logging.info(
    getMessage(messageKey, [sanitisedApiName, requestId]),
    warnHeading,
    warnBold
  );
};

/**
 * Calculates the API response time.
 *
 * @param {string} sanitisedApiName - The sanitised API name.
 * @param {string} requestId - The request ID.
 * @returns {number} - The API response time in seconds.
 */
const calculateApiResponseTime = (
  sanitisedApiName: string,
  requestId: string
): number => {
  const getConfigValue = (apiName: string, requestId: string) =>
    getConfig()?.analyticsApiMilestones?.[apiName]?.[requestId] ?? null;

  const apiObject = getConfigValue(sanitisedApiName, requestId) as {
    time?: number;
  } | null;

  const time = apiObject?.time ?? 0;

  return tools.getTimeElapsedInSeconds(time);
};

/**
 * Sanitises the API name.
 *
 * @param {string} apiName - The original API name.
 * @returns {string} - The sanitised API name.
 */
const sanitiseApiName = (apiName: string): string =>
  apiName
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+/g, ''); // Remove spaces

/**
 * Determines if the event is a start event.
 *
 * @param {string} event - The event type.
 * @returns {boolean} - True if the event is a start event, otherwise false.
 */
const isStartEvent = (event: string): boolean =>
  event === AnalyticsEventTypes.ApiStart ||
  event === AnalyticsEventTypes.TransactionStart;

/**
 * Determines if the event is a complete or error event.
 *
 * @param {string} event - The event type.
 * @returns {boolean} - True if the event is a complete or error event, otherwise false.
 */
const isCompleteOrErrorEvent = (event: string): boolean =>
  event === AnalyticsEventTypes.ApiComplete ||
  event === AnalyticsEventTypes.TransactionComplete ||
  event === AnalyticsEventTypes.ApiError;

/**
 * Handles the start event.
 *
 * @param {Record<string, any>} apiNameMilestone - The API milestone object.
 * @param {string} requestId - The request ID.
 * @param {string} event - The event type.
 * @param {string} sanitisedApiName - The sanitised API name.
 * @param {string} apiNameKey - The API name key.
 * @returns {boolean} - True if the milestone is set, otherwise false.
 */
const handleStartEvent = (
  apiNameMilestone: Record<string, any>,
  requestId: string,
  event: string,
  sanitisedApiName: string,
  apiNameKey: string
): boolean => {
  if (tools.hasApiMilestoneFired(apiNameMilestone, requestId)) {
    logging.info(
      getMessage('apiAlreadyFired', [
        event,
        sanitisedApiName,
        apiNameKey,
        requestId,
      ]),
      infoHeading,
      infoBold
    );
    return false;
  }
  tools.setApiMilestone(apiNameKey, requestId);
  return true;
};

/**
 * Handles the complete or error event.
 *
 * @param {Record<string, any>} apiNameMilestone - The API milestone object.
 * @param {string} requestId - The request ID.
 * @param {string} event - The event type.
 * @param {string} sanitisedApiName - The sanitised API name.
 * @param {string} apiNameKey - The API name key.
 * @returns {number|boolean} - The API response time if successful, otherwise false.
 */
const handleCompleteOrErrorEvent = (
  apiNameMilestone: Record<string, any>,
  requestId: string,
  event: string,
  sanitisedApiName: string,
  apiNameKey: string
): number | boolean => {
  if (!apiNameMilestone || !apiNameMilestone[requestId]) {
    logMissingMilestone(event, sanitisedApiName, requestId);
    return false;
  }

  const timeApiResponse = calculateApiResponseTime(sanitisedApiName, requestId);
  tools.resetApiMilestone(apiNameKey, requestId);
  return timeApiResponse;
};

// API Transaction Handler
export const apiMilestoneHandler = ({
  event = AnalyticsEventTypes.ApiStart ||
    AnalyticsEventTypes.TransactionStart ||
    AnalyticsEventTypes.ApiError,
  data = { requestId: '', apiName: '' },
}: ApiEvent): number | boolean => {
  const { requestId = '', apiName = '' } = data;

  if (!apiName) {
    logWarning('apiNameNotIncluded', event);
    return false;
  }

  const sanitisedApiName = sanitiseApiName(apiName);
  const apiNameKey = `${sanitisedApiName}`;
  const apiMilestones = tools.getAllApiMilestonesFired();
  const apiNameMilestone = apiMilestones[apiNameKey];

  if (isStartEvent(event)) {
    return handleStartEvent(
      apiNameMilestone,
      requestId,
      event,
      sanitisedApiName,
      apiNameKey
    );
  }
  if (isCompleteOrErrorEvent(event)) {
    return handleCompleteOrErrorEvent(
      apiNameMilestone,
      requestId,
      event,
      sanitisedApiName,
      apiNameKey
    );
  }

  return false;
};

// Extract the API Type
export const getApiType = (url: string): string | undefined =>
  url && url.split('/').pop();
